import type { PresenterAuction, PresenterLot } from "./room";

/**
 * Presenter mode's arithmetic, kept out of the components so it can be asserted — the panel has
 * no DOM test environment (`docs/implementation-plan.md`, M0.14 *Carried forward*).
 */

/** How many comments the overlay shows. Read from across a room, so few and large. */
export const PRESENTER_COMMENTS = 6;

/**
 * The lot to put on screen: the one selling, or failing that the one featured.
 *
 * The room can hold two at once — a lot featured while another is still selling — and the one
 * a presenter is talking about is the one that can be bought or bid on.
 */
export function onAirLot(lots: PresenterLot[]): PresenterLot | undefined {
  return (
    lots.find((lot) => lot.state === "selling") ??
    lots.find((lot) => lot.state === "featured")
  );
}

/** The newest `limit` items, oldest first — the order the overlay stacks them in. */
export function recent<T>(items: T[], limit = PRESENTER_COMMENTS): T[] {
  return items.length > limit ? items.slice(items.length - limit) : items;
}

/**
 * Whole seconds left on an auction's clock, or `undefined` where it has none to show.
 *
 * `offsetMs` is the server's clock less this machine's, measured when the snapshot arrived
 * (`R-VID-1`): the deadline is the server's instant, so it is compared with the server's now.
 * Rounded up, so the clock reads `0:01` until the last second has actually gone.
 */
export function secondsLeft(
  auction: PresenterAuction,
  now: number,
  offsetMs: number,
): number | undefined {
  if (auction.state !== "open") return undefined;
  if (auction.paused) {
    return auction.remainingMs === null
      ? undefined
      : Math.max(0, Math.ceil(auction.remainingMs / 1000));
  }
  if (auction.deadline === null) return undefined;
  const left = Date.parse(auction.deadline) - (now + offsetMs);
  return Number.isFinite(left) ? Math.max(0, Math.ceil(left / 1000)) : undefined;
}

/** Whole seconds left on a timed fixed-price lot (`R-LOT-34`), on the server's clock. */
export function saleSecondsLeft(
  closesAt: string,
  now: number,
  offsetMs: number,
): number {
  return Math.max(0, Math.ceil((Date.parse(closesAt) - (now + offsetMs)) / 1000));
}

/** `m:ss`, the way the room writes a clock. */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
